import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import { Minus, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { gsap } from '@/lib/gsap'

import {
    ADVANTAGES,
    ADVANTAGES_COPY,
    STATUS_ITEMS
} from '@/constants/aerive'

const formatTime = (date: Date): string => date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })

const Advantages: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const [active, setActive] = useState(ADVANTAGES[0].id)
    const [time, setTime] = useState('')

    // The visitor's own clock, read after mount so the server and browser agree
    useEffect(() => {
        const tick = (): void => setTime(formatTime(new Date()))
        tick()
        const timer = window.setInterval(tick, 15000)
        return () => window.clearInterval(timer)
    }, [])

    useEffect(() => {
        const section = sectionRef.current
        if (!section) return

        const mm = gsap.matchMedia()

        mm.add({
            motion: '(prefers-reduced-motion: no-preference)',
            reduce: '(prefers-reduced-motion: reduce)'
        }, (context) => {
            const { motion } = context.conditions as { motion: boolean }

            section.querySelectorAll<HTMLElement>('[data-reveal]').forEach((element) => {
                gsap.fromTo(
                    element,
                    motion ? { autoAlpha: 0, y: 60, filter: 'blur(8px)' } : { autoAlpha: 0 },
                    {
                        autoAlpha: 1,
                        y: 0,
                        filter: 'blur(0px)',
                        ease: 'power2.out',
                        scrollTrigger: {
                            trigger: element,
                            start: 'top 92%',
                            end: 'top 62%',
                            scrub: 1,
                            refreshPriority: 6
                        }
                    }
                )
            })

            const media = section.querySelector<HTMLElement>('[data-advantage-media]')
            if (media && motion) {
                gsap.fromTo(media, { clipPath: 'inset(18% 12% 18% 12% round 4px)' }, {
                    clipPath: 'inset(0% 0% 0% 0% round 4px)',
                    ease: 'none',
                    scrollTrigger: {
                        trigger: media,
                        start: 'top 95%',
                        end: 'top 40%',
                        scrub: 1,
                        refreshPriority: 6
                    }
                })
            }
        })

        return () => mm.revert()
    }, [])

    return (
        <section
            id='advantages'
            ref={sectionRef}
            aria-labelledby='advantages-title'
            className='relative bg-ivory px-5 pb-12 pt-24 text-obsidian md:px-[6vw] md:pb-16 md:pt-36'
        >
            <div data-reveal className='flex items-center justify-between border-t border-obsidian/15 pt-5'>
                <p className='text-[11px] uppercase tracking-[0.25em] text-umber'>{ADVANTAGES_COPY.eyebrow}</p>
                <p className='text-[11px] uppercase tracking-[0.25em] text-ash'>Advantages</p>
            </div>

            <div className='advantages-grid mt-14 md:mt-20'>
                <div className='[grid-area:head]'>
                    <h2 id='advantages-title' data-reveal className='font-heading text-[clamp(3rem,5.6vw,6rem)] font-light leading-[0.92] tracking-[-0.01em]'>
                        {ADVANTAGES_COPY.title[0]}
                        <br />
                        {ADVANTAGES_COPY.title[1]}
                    </h2>
                    <p data-reveal className='mt-6 max-w-md text-[15px] leading-relaxed text-ash'>
                        {ADVANTAGES_COPY.intro}
                    </p>
                </div>

                <div
                    data-advantage-media
                    className='relative aspect-[4/5] w-full self-start overflow-hidden rounded-[4px] bg-onyx [grid-area:media]'
                >
                    {ADVANTAGES.map((item) => {
                        const isActive = item.id === active
                        const crop = item.crop ?? 1

                        return (
                            <div
                                key={item.id}
                                className={cn(
                                    'absolute inset-0 transition-[opacity,transform] duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
                                    isActive ? 'opacity-100' : 'opacity-0'
                                )}
                                style={{ transform: `scale(${crop * (isActive ? 1 : 1.06)})` }}
                                aria-hidden={!isActive}
                            >
                                <Image
                                    src={item.image}
                                    alt={item.alt}
                                    fill
                                    sizes='(max-width: 1023px) 90vw, 44vw'
                                    quality={82}
                                    className='object-cover'
                                />
                            </div>
                        )
                    })}
                    <div className='pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-linear-to-t from-obsidian/45 to-transparent' />
                    <p className='absolute bottom-5 left-5 text-[10px] uppercase tracking-[0.25em] text-ivory/85'>
                        {ADVANTAGES.find((item) => item.id === active)?.title}
                    </p>
                </div>

                <div className='[grid-area:list] lg:mt-14'>
                    <div data-reveal className='border-t border-obsidian/15'>
                        {ADVANTAGES.map((item) => {
                            const isOpen = item.id === active

                            return (
                                <div key={item.id} className='border-b border-obsidian/15'>
                                    <h3>
                                        <button
                                            type='button'
                                            id={`advantage-${item.id}`}
                                            aria-expanded={isOpen}
                                            aria-controls={`advantage-panel-${item.id}`}
                                            onClick={() => setActive(item.id)}
                                            className='group flex w-full items-center justify-between gap-6 py-5 text-left focus-visible:outline-none md:py-6'
                                        >
                                            <span className={cn(
                                                'font-heading text-[1.9rem] font-normal leading-none transition-colors duration-500 md:text-[2.2rem]',
                                                isOpen ? 'text-obsidian' : 'text-obsidian/55 group-hover:text-obsidian group-focus-visible:text-obsidian'
                                            )}
                                            >
                                                {item.title}
                                            </span>
                                            <span className='flex size-9 shrink-0 items-center justify-center rounded-full border border-obsidian/20 text-obsidian transition-colors group-hover:bg-obsidian group-hover:text-ivory group-focus-visible:bg-obsidian group-focus-visible:text-ivory'>
                                                {isOpen ? <Minus className='size-4' /> : <Plus className='size-4' />}
                                            </span>
                                        </button>
                                    </h3>
                                    <div
                                        id={`advantage-panel-${item.id}`}
                                        role='region'
                                        aria-labelledby={`advantage-${item.id}`}
                                        data-open={isOpen}
                                        className='accordion-panel'
                                    >
                                        <div className='overflow-hidden'>
                                            <p className='max-w-md pb-7 pr-12 text-[15px] leading-relaxed text-ash'>
                                                {item.text}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>

            <div data-reveal className='mt-24 grid grid-cols-2 gap-y-8 border-t border-obsidian/15 pt-6 md:mt-32 md:grid-cols-3'>
                {STATUS_ITEMS.map((item) => (
                    <div key={item.label}>
                        <p className='text-[10px] uppercase tracking-[0.24em] text-ash'>{item.label}</p>
                        <p className='mt-2 text-[14px] font-medium text-obsidian'>{item.value}</p>
                    </div>
                ))}
                <div className='col-span-2 flex items-end justify-between md:col-span-1 md:block md:text-right'>
                    <p className='text-[10px] uppercase tracking-[0.24em] text-ash'>Your local time</p>
                    <p className='font-heading text-[2.6rem] font-light leading-none tabular-nums text-obsidian md:mt-1'>
                        {time || <>&nbsp;</>}
                    </p>
                </div>
            </div>
        </section>
    )
}

export default Advantages
